// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux'
import { useIntl } from "react-intl";
import { Typography, makeStyles, Box } from '@material-ui/core';
import { debounce } from 'lodash';

import { FormTextField, Loading, AlertMessages, AlertDialog, FormActionButtons } from '../../components';

import { getI18n } from "../../utils/intl";
import { ALERT_ERROR, ALERT_SUCCESS } from "../../constants";
import { getTenantsInfo, getUpdateTenantsInfoResponse } from '../../reducers/settings';
import { fetchTenantInfos, updateTenantInfos, updateTrackingId } from "../../actions/settings";

import { settingsReducer, initializeSettingsState, ACTIONS, validateFormInputs } from "./reducer.js";
import styles from './styles';
import { getIsPortalAdmin } from '../../reducers/portalConfig/index.js';
import { useHistory } from 'react-router';

const labels = {
  pageName: "label.settings.title",
  header: "label.portal.setup.title",
  adminEmail: "label.portal.setup.admin.email.title",
  trackingId: "label.portal.setup.google-analytics.title",
  cancelButton: "label.cancel.button",
  saveButton: "label.save.button",
  loadingError: "label.portal.setup.error.load",
  unsavedChangesTitle: "label.dialog.unsaved.changes.title",
  unsavedChangesDescription: "label.category.list.page.unsaved.changes",
  unsavedChangesCancelText: "label.cancel.button",
  saveSuccessText: "label.save.success.text",
};

const helpTexts = {
  adminEmail: "label.portal.setup.admin.email.helptext",
  trackingId: "label.portal.setup.google-analytics.helptext",
};

const validateFieldValue = debounce(({ field, dispatch, intl, regexPolicy }) => {
  dispatch({ type: ACTIONS.UPDATE_FIELD_ERRORS, payload: { field, intl, regexPolicy } })
}, 300);

const getIsFormDirty = (state, initialData) => {
  if (state.adminEmail.value !== (initialData?.adminEmail || '')) return true;
  if (state.trackingId.value !== (initialData?.trackingId || '')) return true;
  return false;
};

const Settings = () => {
  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const globalDispatch = useDispatch();

  const initialData = useSelector(getTenantsInfo);
  const updatedData = useSelector(getUpdateTenantsInfoResponse);

  const [state, dispatch] = React.useReducer(
    settingsReducer, null, initializeSettingsState,
  );
  const [notification, setNotification] = React.useState({ status: '', message: '' });
  const [isUnsavedAlertDialogOpen, setIsUnsavedAlertDialogOpen] = React.useState(false);
  const [isSettingsSuccess, setIsSettingsSuccess] = React.useState(false);

  React.useEffect(() => {
    globalDispatch(fetchTenantInfos());
  }, []);

  React.useEffect(() => {
    if (initialData.error) {
      setNotification({ status: ALERT_ERROR, message: initialData.error });
    }
    if (initialData.data) {
      const data = {
        adminEmail: initialData.data.adminEmail,
        trackingId: initialData.data.trackingId,
      }
      dispatch({ type: ACTIONS.UPDATE_FORM_VALUES, payload: { intl, data } });
    }
  }, [initialData]);

  React.useEffect(() => {
    if (updatedData.data) {
      setNotification({
        status: ALERT_SUCCESS,
        message: intl.getI18nMessage(labels.saveSuccessText),
      });
      setTimeout(() => setNotification({ status: '', message: '' }), 2500);
      setIsSettingsSuccess(true);
      globalDispatch(fetchTenantInfos());
      return;
    }
    if (updatedData.error !== null) {
      if (updatedData.error === '') {
        setNotification({ status: ALERT_ERROR, message: intl.getI18nMessage("label.unknown.error") });
        return;
      }

      if (updatedData.error?.errorCode) {
        setNotification({
          status: ALERT_ERROR,
          message: intl.getI18nMessage("label.error.code",
            { code: updatedData.error.errorCode }),
        });
        return;
      }

      setNotification({ status: ALERT_ERROR, message: updatedData.error });
    }
  }, [updatedData]);

  const isFormDirty = React.useMemo(() => {
    setIsSettingsSuccess(false);
    return getIsFormDirty(state, initialData.data)
  }, [state, initialData.data]);

  const { isFormValid } = React.useMemo(() => validateFormInputs({ state, intl }), [state, intl]);

  const handleChange = (field, value) => {
    dispatch({ type: ACTIONS.UPDATE_FIELD_VALUE, payload: { field, value } });
    validateFieldValue({ field, dispatch, intl });
  }

  const onSave = () => {
    const { validatedState, isFormValid } = validateFormInputs({ state, intl });
    if (isFormValid) {
      if(state.adminEmail.value !== initialData?.data.adminEmail) {
        globalDispatch(updateTenantInfos({
          adminEmail : state.adminEmail.value,
        }));
      }
      if(state.trackingId.value !== initialData?.data.trackingId) {
        globalDispatch(updateTrackingId({
          trackingId : state.trackingId.value,
        }));
      }
    } else {
      dispatch({ type: ACTIONS.UPDATE_FORM_ERRORS, payload: validatedState });
    }
  }

  const onCancel = () => {
    if (isFormDirty) {
      setIsUnsavedAlertDialogOpen(true);
    }
  };

  const onUnsavedAlertDialogClose = () => {
    setIsUnsavedAlertDialogOpen(false);
  };

  const onUnsavedAlertDialogSubmit = () => {
    if (initialData.data) {
      const data = {
        adminEmail: initialData.data.adminEmail,
        trackingId: initialData.data.trackingId,
      }
      dispatch({ type: ACTIONS.UPDATE_FORM_VALUES, payload: { intl, data } });
    }
    setIsUnsavedAlertDialogOpen(false);
  };

  const showLoadingOverlay = initialData.isLoading || updatedData.isLoading;

  const isPortalAdmin = useSelector((state) => getIsPortalAdmin(state));
  const history = useHistory();

  useEffect(() => {
    if (!isPortalAdmin) {
      history.push('/404')
    }
  }, [isPortalAdmin])

  return <>
    {showLoadingOverlay && <Loading pageLoader />}

    <Box className={classes.contentContainer}>
      <Box className={classes.header}>
        <Typography variant='h6'>
          {intl.getI18nMessage(labels.pageName)}
        </Typography>
        <Typography
          data-apim-test={'settings-header'}
          variant='h1' className={classes.headerText}
        >
          {intl.getI18nMessage(labels.header)}
        </Typography>
      </Box>
      <form className={classes.settingsForm}>
        <FormTextField
          id='settings-admin-email'
          name={intl.getI18nMessage(labels.adminEmail)}
          textFieldClass={classes.textField}
          fieldContainerClass={classes.fieldContainer}
          helperText={
            state.adminEmail.errors.length > 0
              ? state.adminEmail.errors[0]
              : intl.getI18nMessage(helpTexts.adminEmail)
          }
          errorHelperText={state.adminEmail.errors.length > 0}
          error={state.adminEmail.errors.length > 0}
          value={state.adminEmail.value}
          handleChange={value => handleChange('adminEmail', value)}
          maxLength="254"
        />

        <FormTextField
          id='settings-tracking-id'
          name={intl.getI18nMessage(labels.trackingId)}
          textFieldClass={classes.textField}
          fieldContainerClass={classes.fieldContainer}
          helperText={
            state.trackingId.errors.length > 0
              ? state.trackingId.errors[0]
              : intl.getI18nMessage(helpTexts.trackingId)
          }
          errorHelperText={state.trackingId.errors.length > 0}
          error={state.trackingId.errors.length > 0}
          value={state.trackingId.value}
          handleChange={value => handleChange('trackingId', value)}
          maxLength="18"
          placeholder="UA-XXXXX-XX"
        />

        {isFormDirty && isFormValid && !isSettingsSuccess && <FormActionButtons
          id={"settings"}
          isContainerFooter
          cancelText={intl.getI18nMessage(labels.cancelButton)}
          nextText={intl.getI18nMessage(labels.saveButton)}
          onCancelClick={onCancel}
          onNextClick={onSave}
          isDisabled={initialData.error || updatedData.isLoading}
        />}
      </form>
    </Box>

    {notification.message && (
      <AlertMessages
        className={classes.notification}
        id="notificationId"
        data-apim-test="notificationId"
        variant={notification.status}
        message={notification.message}
        onClose={() => setNotification({ status: "", message: "" })}
        shouldAutoCloseSuccessMessages={notification.status === ALERT_SUCCESS}
      />
    )}

    <AlertDialog
      isOpen={isUnsavedAlertDialogOpen}
      title={intl.getI18nMessage(labels.unsavedChangesTitle)}
      description={intl.getI18nMessage(labels.unsavedChangesDescription)}
      onClose={onUnsavedAlertDialogClose}
      onSubmit={onUnsavedAlertDialogSubmit}
      onCancel={onUnsavedAlertDialogClose}
      dialogId="settings-unsaved-dialog"
      submitText={intl.getI18nMessage('label.ok.button')}
      cancelText={intl.getI18nMessage(labels.unsavedChangesCancelText)}
    />
  </>
}

export default Settings;
